import memoizeOne from 'memoize-one';

export interface Permissions {
  can: (resource: unknown, permission: unknown) => boolean;
  canSee: (resource: unknown, permission: unknown) => boolean;
  resourcify: (obj: unknown, resource: unknown) => unknown;
  _key?: unknown;
}

type Checker = (permission: unknown, resource?: unknown, obj?: unknown) => boolean;

export const createPermissionsChecker =
  (permissions: Permissions, isSee?: boolean): Checker =>
  (permission, resource, obj) => {
    return isSee
      ? permissions.canSee(permissions.resourcify(obj, resource), permission)
      : permissions.can(permissions.resourcify(obj, resource), permission);
  };

const createPermissionsRenderHelper =
  (canChecker: Checker, canSeeChecker: Checker) =>
  (permission: unknown, resource: unknown, obj: unknown, render?: unknown) => {
    const renderFunc = (render ? render : obj) as (can: boolean, canSee: boolean) => unknown;

    const can = canChecker(permission, resource, obj);
    const canSee = canSeeChecker(permission, resource, obj);
    return can || canSee ? renderFunc(can, canSee) : null;
  };

/*
Which permissions checker should I use?

* can - use when you only care about if user can do action or not - using this carries a message
  that action is visible only when user can actually do it.
* canSee - use it when you want to show that user could do an action, even
  if he currently can't. For example - like a post as logged in, but private member.
  Using this should be accompanied by decorating for user to login or become a public member.
* canRender - use it everywhere where you render UI based on permissions. It checks
  both can and canSee.

*/

const create = (permissions: Permissions) => {
  const can = createPermissionsChecker(permissions, false);
  const canSee = createPermissionsChecker(permissions, true);
  const canRender = createPermissionsRenderHelper(can, canSee);
  return { can, canSee, canRender };
};

const createMemoized = memoizeOne(
  create,
  (newArgs, lastArgs) => newArgs[0]?._key === lastArgs[0]?._key,
);

export default function createPermissionHelpers(permissions: Permissions) {
  return permissions._key ? createMemoized(permissions) : create(permissions);
}
